// src/components/RPA/HumanFindingsPanel.js
// ⭐ NEW: Human-in-the-Loop Feature 3 — Independent Auditor Findings entry.
//correct vs
// The auditor assesses each material account independently of the AI:
// per-account 'no exception' attestations or documented exceptions with
// amount and severity. Draft saves are repeatable; SUBMIT permanently locks
// the record (submittedAt = independence evidence) and lifts the blind.
//
// Props:
//   doc             — the RPADocument row object
//   comparisonInfo  — { mode, blindActive, humanFindingsSubmittedAt } from
//                     GET /admin/rpa/docs/:docId/comparison-mode
//   onChanged       — callback after a successful save/submit (refresh docs)

import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const SEVERITIES = ['low', 'moderate', 'high', 'critical'];

const parseMaybeJSON = (v) => {
  if (!v) return null;
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch { return null; }
};

// Build the account universe the auditor can assess. The balance sheet alone is
// too narrow — the engagement covers the full trial balance, the income
// statement, and the accounts the AI itself flagged as significant.
// Sources, in priority order:
//   1. trialBalance.data[]              — authoritative chart of accounts
//   2. incomeStatement.data[]           — revenue/expense lines (often absent from the TB)
//   3. extractedBalances.accounts[]     — balance sheet as extracted
//   4. financialStatementAnalysis       — AI-flagged significant + cash accounts
const getAccountSuggestions = (doc) => {
  const bsa = doc?.balanceSheetAudit || {};
  const names = new Map(); // normalized key → display name (first wins)

  const add = (raw) => {
    const name = String(raw || '').trim();
    if (!name) return;
    const key = name.toLowerCase().replace(/\s+/g, ' ');
    if (!names.has(key)) names.set(key, name);
  };

  // Rows from a CSV-style upload: trialBalance / incomeStatement both store { data: [...] }
  const harvestRows = (container) => {
    const parsed = parseMaybeJSON(container);
    const rows = Array.isArray(parsed?.data) ? parsed.data
               : Array.isArray(parsed?.rows) ? parsed.rows
               : Array.isArray(parsed)       ? parsed
               : null;
    if (!rows) return;
    rows.forEach(r => add(
      r?.Account || r?.account || r?.accountName || r?.account_name ||
      r?.LineItem || r?.line_item || r?.Description || r?.name
    ));
  };

  harvestRows(bsa.trialBalance);
  harvestRows(bsa.incomeStatement);

  // Balance sheet accounts as extracted
  const bal = parseMaybeJSON(bsa.extractedBalances);
  const harvestAccounts = (arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach(a => add(a?.account_name || a?.account || a?.name || a?.line_item));
  };
  if (bal) {
    harvestAccounts(bal.accounts);
    harvestAccounts(bal.balances);
    harvestAccounts(bal.line_items);
    harvestAccounts(bal.assets);
    harvestAccounts(bal.liabilities);
    harvestAccounts(bal.equity);
  }

  // AI-flagged accounts from the financial statement analysis
  const fsa = parseMaybeJSON(bsa.financialStatementAnalysis || doc?.financialStatementAnalysis);
  if (fsa) {
    harvestAccounts(fsa.significant_accounts);
    if (Array.isArray(fsa.cash_accounts)) fsa.cash_accounts.forEach(a => add(a?.account_name));
  }

  return Array.from(names.values());
};

const emptyEntry = () => ({
  account: '',
  assessment: 'no_exception',
  issue: '',
  amount: '',
  severity: 'moderate'
});

const HumanFindingsPanel = ({ doc, comparisonInfo, onChanged }) => {
  const docId = doc?._id;
  const existing = doc?.balanceSheetAudit?.humanFindings || null;
  const locked = !!existing?.submittedAt;

  const [entries, setEntries] = useState([emptyEntry()]);
  const [generalNotes, setGeneralNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const suggestions = getAccountSuggestions(doc);
  const blindActive = !!comparisonInfo?.blindActive;
  const mode = comparisonInfo?.mode || 'blind';

  // Hydrate from an existing draft/submission
  useEffect(() => {
    if (existing?.entries?.length) {
      setEntries(existing.entries.map(e => ({
        account: e.account || '',
        assessment: e.assessment || 'no_exception',
        issue: e.issue || '',
        amount: e.amount === null || e.amount === undefined ? '' : String(e.amount),
        severity: e.severity || 'moderate'
      })));
    }
    if (existing?.generalNotes) setGeneralNotes(existing.generalNotes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, existing?.submittedAt, existing?.draftSavedAt]);

  const updateEntry = (i, patch) =>
    setEntries(prev => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));

  const addRow = () => setEntries(prev => [...prev, emptyEntry()]);
  const removeRow = (i) => setEntries(prev => prev.filter((_, idx) => idx !== i));

  const buildPayloadEntries = () =>
    entries
      .filter(e => e.account.trim())
      .map(e => ({
        account: e.account.trim(),
        assessment: e.assessment,
        issue: e.assessment === 'exception' ? e.issue.trim() : null,
        amount: e.assessment === 'exception' && e.amount !== '' ? Number(e.amount) : null,
        severity: e.assessment === 'exception' ? e.severity : null
      }));

  const validate = (payloadEntries, forSubmit) => {
    if (forSubmit && payloadEntries.length === 0) return 'Add at least one account assessment before submitting.';
    for (const e of payloadEntries) {
      if (e.assessment === 'exception' && !e.issue) {
        return `"${e.account}": an exception requires a documented issue description.`;
      }
    }
    return null;
  };

  const save = async (submit) => {
    setError('');
    setStatus('');
    const payloadEntries = buildPayloadEntries();
    const validationError = validate(payloadEntries, submit);
    if (validationError) { setError(validationError); return; }

    submit ? setSubmitting(true) : setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/rpa/docs/${docId}/human-findings`,
        { entries: payloadEntries, generalNotes, submit },
        { withCredentials: true }
      );
      if (data.success) {
        setStatus(data.message || (submit ? 'Submitted and locked.' : 'Draft saved.'));
        setConfirmSubmit(false);
        if (typeof onChanged === 'function') onChanged();
      } else {
        setError(data.message || 'Save failed.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  if (!docId) return null;

  const exceptionCount = entries.filter(e => e.account.trim() && e.assessment === 'exception').length;

  // ── Locked (submitted) view ──
  if (locked) {
    return (
      <div style={{ border: '1px solid #22c55e', borderRadius: 10, background: '#f0fdf4', padding: 16, marginTop: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#166534', marginBottom: 6 }}>
          🔒 Independent Auditor Findings — Submitted &amp; Locked
        </div>
        <div style={{ fontSize: 12, color: '#166534', marginBottom: 10 }}>
          Submitted {new Date(existing.submittedAt).toLocaleString()}
          {existing.auditor?.name ? ` by ${existing.auditor.name}` : ''} · {existing.entries?.length || 0} account
          {(existing.entries?.length || 0) !== 1 ? 's' : ''} assessed. This record is permanently locked; variances are
          resolved in the comparison workpaper below.
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          {(existing.entries || []).map((e, i) => (
            <div key={i} style={{
              display: 'flex', gap: 10, alignItems: 'center', fontSize: 12,
              background: '#fff', border: '1px solid #dcfce7', borderRadius: 6, padding: '6px 10px'
            }}>
              <strong style={{ minWidth: 180 }}>{e.account}</strong>
              {e.assessment === 'exception' ? (
                <>
                  <span style={{ color: '#dc2626', fontWeight: 600 }}>Exception</span>
                  {Number.isFinite(Number(e.amount)) && e.amount !== null && (
                    <span>${Math.abs(Number(e.amount)).toLocaleString()}</span>
                  )}
                  <span style={{ color: '#6b7280' }}>{e.severity}</span>
                  <span style={{ color: '#374151', flex: 1 }}>{e.issue}</span>
                </>
              ) : (
                <span style={{ color: '#16a34a', fontWeight: 600 }}>No exception</span>
              )}
            </div>
          ))}
        </div>
        {existing.generalNotes && (
          <div style={{ fontSize: 12, color: '#374151', marginTop: 8 }}>
            <strong>Notes:</strong> {existing.generalNotes}
          </div>
        )}
      </div>
    );
  }

  // ── Entry form ──
  return (
    <div style={{ border: '1px solid #7c3aed', borderRadius: 10, background: '#faf5ff', padding: 16, marginTop: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#5b21b6', marginBottom: 4 }}>
        ✍️ Independent Auditor Findings {mode === 'blind' ? '(Blind Entry)' : '(Review Mode)'}
      </div>
      <div style={{ fontSize: 12, color: '#6d28d9', marginBottom: 12 }}>
        {blindActive
          ? 'Blind mode is active: AI findings, opinion, and findings anomalies are hidden until you submit. Assess each material account from the evidence sections above. Submission is permanent and cannot be edited.'
          : mode === 'blind'
            ? 'Enter your independent assessment per material account. Submission is permanent and cannot be edited.'
            : 'Review mode: AI results are visible. Enter your assessments per material account; submission is permanent and creates the comparison workpaper.'}
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {entries.map((e, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #e9d5ff', borderRadius: 8, padding: 10 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                list={`hfp-accounts-${docId}`}
                placeholder="Account (e.g. Accounts receivable, net)"
                value={e.account}
                onChange={ev => updateEntry(i, { account: ev.target.value })}
                style={{ flex: '1 1 220px', minWidth: 200, padding: '6px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }}
              />
              <select
                value={e.assessment}
                onChange={ev => updateEntry(i, { assessment: ev.target.value })}
                style={{ padding: '6px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }}
              >
                <option value="no_exception">✅ No exception</option>
                <option value="exception">⚠️ Exception identified</option>
              </select>
              {e.assessment === 'exception' && (
                <>
                  <input
                    type="number"
                    placeholder="Amount ($)"
                    value={e.amount}
                    onChange={ev => updateEntry(i, { amount: ev.target.value })}
                    style={{ width: 110, padding: '6px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }}
                  />
                  <select
                    value={e.severity}
                    onChange={ev => updateEntry(i, { severity: ev.target.value })}
                    style={{ padding: '6px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }}
                  >
                    {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </>
              )}
              {entries.length > 1 && (
                <button onClick={() => removeRow(i)} style={{ border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: 14 }} title="Remove row">✕</button>
              )}
            </div>
            {e.assessment === 'exception' && (
              <textarea
                placeholder="Describe the exception: nature, evidence reference, basis for the amount…"
                value={e.issue}
                onChange={ev => updateEntry(i, { issue: ev.target.value })}
                rows={2}
                style={{ width: '100%', marginTop: 8, padding: '6px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, resize: 'vertical', boxSizing: 'border-box' }}
              />
            )}
          </div>
        ))}
      </div>

      <datalist id={`hfp-accounts-${docId}`}>
        {suggestions.map(a => <option key={a} value={a} />)}
      </datalist>

      <button onClick={addRow} style={{ marginTop: 8, fontSize: 12, padding: '5px 10px', border: '1px dashed #7c3aed', color: '#7c3aed', background: '#fff', borderRadius: 6, cursor: 'pointer' }}>
        + Add account
      </button>

      <textarea
        placeholder="General notes / basis for overall assessment (optional)"
        value={generalNotes}
        onChange={ev => setGeneralNotes(ev.target.value)}
        rows={2}
        style={{ width: '100%', marginTop: 10, padding: '6px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, resize: 'vertical', boxSizing: 'border-box' }}
      />

      {error && <div style={{ marginTop: 8, fontSize: 12, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px' }}>{error}</div>}
      {status && <div style={{ marginTop: 8, fontSize: 12, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '6px 10px' }}>{status}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => save(false)}
          disabled={saving || submitting}
          style={{ fontSize: 12, padding: '7px 14px', border: '1px solid #7c3aed', color: '#7c3aed', background: '#fff', borderRadius: 6, cursor: 'pointer' }}
        >
          {saving ? 'Saving…' : '💾 Save Draft'}
        </button>

        {!confirmSubmit ? (
          <button
            onClick={() => { setError(''); setConfirmSubmit(true); }}
            disabled={saving || submitting}
            style={{ fontSize: 12, padding: '7px 14px', border: 'none', color: '#fff', background: '#7c3aed', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
          >
            🔒 Submit &amp; Lock Findings
          </button>
        ) : (
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 12, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 6, padding: '6px 10px' }}>
            <span style={{ color: '#92400e' }}>
              Permanent: {entries.filter(e => e.account.trim()).length} assessment(s), {exceptionCount} exception(s). Cannot be edited after submission.
            </span>
            <button
              onClick={() => save(true)}
              disabled={submitting}
              style={{ fontSize: 12, padding: '5px 12px', border: 'none', color: '#fff', background: '#dc2626', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              {submitting ? 'Submitting…' : 'Confirm Submit'}
            </button>
            <button
              onClick={() => setConfirmSubmit(false)}
              disabled={submitting}
              style={{ fontSize: 12, padding: '5px 10px', border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </span>
        )}
      </div>
    </div>
  );
};

export default HumanFindingsPanel;